
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';
import Img from 'theme/components/shared/Img'


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "glossary"
    }}>{`Glossary`}</h1>
    <p>{`A Glossary provides appropriate vocabularies for business users, and it allows the terms (words) to be related to each
other and categorized so that they can be understood in different contexts. These terms can be then mapped to assets
like a Database, tables, columns etc. This helps abstract the technical jargon associated with the repositories and
allows the user to discover/work with data in the vocabulary that is more familiar to them.`}</p>
    <h3 {...{
      "id": "use-cases"
    }}>{`Use cases`}</h3>
    <ul>
      <li parentName="ul">{`Ability to define rich glossary vocabularies using the natural terminology (technical terms and/or business terms).`}</li>
      <li parentName="ul">{`Ability to semantically relate the term(s) to each other.`}</li>
      <li parentName="ul">{`Ability to map assets to glossary terms(s).`}</li>
      <li parentName="ul">{`Ability to organize these terms by categories. This adds more context to the term(s).`}</li>
      <li parentName="ul">{`Allow categories to be arranged in hierarchy - to express broader and finer scopes.`}</li>
      <li parentName="ul">{`Separate management of glossary term(s) from the metadata.`}</li>
    </ul>
    <h3 {...{
      "id": "what-is-a-glossary-term-"
    }}>{`What is a Glossary term ?`}</h3>
    <p>{`A term is a useful word for an enterprise. For the term(s) to be useful and meaningful, they need to grouped around their
use and context. A term in Apache Atlas must have a unique qualifiedName, there can be term(s) with same name, but they
cannot belong to the same glossary. Term(s) with same name can exist only across different glossaries. A term name can
contain spaces, underscores and dashes (as natural ways of referring to words) but no "." or "@", as the qualifiedName
takes the following form `}<inlineCode parentName="p">{`term name`}</inlineCode>{`@`}<inlineCode parentName="p">{`glossary qualified name`}</inlineCode>{`. The fully qualified name makes it easier to work with
a specific term.`}</p>
    <p>{`A term can only belong to single glossary, and it's lifecycle is bound to the same i.e. if the Glossary is deleted then
the term gets deleted as well. A term can belong to zero or more categories, which allows scoping them into narrower or
wider contexts. A term can be assigned/linked to zero or more entities in Apache Atlas. A term can be classified using
classifications (tags) and the same classification gets applied to the entities that the term is assigned to.`}</p>
    <h3 {...{
      "id": "what-is-a-glossary-category-"
    }}>{`What is a Glossary category ?`}</h3>
    <p>{`A category is a way of organizing the term(s) so that the term's context can be enriched. A category may or may not have
contained hierarchies i.e. child category hierarchy. A category's qualifiedName is derived using its hierarchical location
within the glossary e.g. `}<inlineCode parentName="p">{`Category name`}</inlineCode>{`.`}<inlineCode parentName="p">{`parent category qualifiedName`}</inlineCode>{`. This qualified name gets updated when any
hierarchical change happens, e.g. addition of a parent category, removal of parent category or change of parent category.`}</p>
    <h3 {...{
      "id": "ui-interactions"
    }}>{`UI interactions`}</h3>
    <p>{`Apache Atlas UI has been updated to provide user-friendly interface to work with various aspects of glossary, including:`}</p>
    <ul>
      <li parentName="ul">{`create glossaries, terms and categories`}</li>
      <li parentName="ul">{`create various relationships between terms - like synonyms, antonyms, seeAlso`}</li>
      <li parentName="ul">{`organize categories in hierarchies`}</li>
      <li parentName="ul">{`assign terms to entities`}</li>
      <li parentName="ul">{`search for entities using associated terms`}</li>
    </ul>
    <p>{`Most glossary related UI can be found under a new tab named GLOSSARY, which is present right next to existing
familiar tabs SEARCH and CLASSIFICATION.`}</p>
    <h4 {...{
      "id": "glossary-tab"
    }}><strong parentName="h4">{`Glossary tab`}</strong></h4>
    <p>{`Apache Atlas UI provides two ways to work with a glossary - term view and category view.`}</p>
    <p>{`Term view allows a user to perform the following operations:`}</p>
    <ul>
      <li parentName="ul">{`create, update and delete terms`}</li>
      <li parentName="ul">{`add, remove and update classifications associated with a term`}</li>
      <li parentName="ul">{`add, remove and update categorization of a term`}</li>
      <li parentName="ul">{`create various relationships between terms`}</li>
      <li parentName="ul">{`view entities associated with a term`}</li>
    </ul>
    <p>{`Category view allows a user to perform the following operations:`}</p>
    <ul>
      <li parentName="ul">{`create, update and delete categories and sub-categories`}</li>
      <li parentName="ul">{`associate terms to categories`}</li>
    </ul>
    <p>{`Users can switch between term view and category view using toggle provided in GLOSSARY tab.`}</p>
    <Img src={`/images/markdown/terms_view.png`} width="300px" mdxType="Img" />
    <Img src={`/images/markdown/category_view_1.png`} width="300px" mdxType="Img" />
    <h5 {...{
      "id": "term-context-menu"
    }}>{`Term context menu`}</h5>
    <ul>
      <li parentName="ul">{`Create a new term
Clicking on the `}<strong parentName="li">{`ellipsis (...)`}</strong>{` next to a glossary name shows a pop-over menu that allows users to create a term in
the glossary or delete the glossary - as shown below.`}</li>
    </ul>
    <Img src={`/images/markdown/term_view_context.png`} width="360px" mdxType="Img" />
    <ul>
      <li parentName="ul">{`To delete a term
Clicking on the `}<strong parentName="li">{`ellipsis (...)`}</strong>{` next to a term name shows a pop-over menu that allows users to delete the term - as
shown below.`}</li>
    </ul>
    <Img src={`/images/markdown/term_delete_context.png`} width="600px" mdxType="Img" />
    <h5 {...{
      "id": "term-detail-page"
    }}>{`Term detail page`}</h5>
    <p>{`Various details of a term can be viewed by clicking on the term name in the glossary UI. Each tabs under the details
page provides different details of the term.`}</p>
    <ul>
      <li parentName="ul">{`Entities tab shows the entities that are assigned to the selected term`}</li>
      <li parentName="ul">{`Classifications tab shows the classification(s) associated with the selected term`}</li>
      <li parentName="ul">{`Related terms tab shows the terms that are related to the selected term`}</li>
    </ul>
    <Img src={`/images/markdown/term_details.png`} mdxType="Img" />
    <h5 {...{
      "id": "add-classification-to-term"
    }}>{`Add classification to Term`}</h5>
    <p>{`Clicking on `}<strong parentName="p">{`+`}</strong>{` next to classification label to add a classification to the term.`}</p>
    <Img src={`/images/markdown/term_add_classification_1.png`} alt="Add classification" width="400px" mdxType="Img" />
    <Img src={`/images/markdown/term_add_classification_2.png`} alt="Add classification - details" width="400px" mdxType="Img" />
    <Img src={`/images/markdown/term_with_classification.png`} alt="Classifications associated with term" width="400px" mdxType="Img" />
    <h5 {...{
      "id": "create-term-relationship-with-other-term"
    }}>{`Create term relationship with other term`}</h5>
    <p>{`Click on "Related Terms" tab when viewing term details. Clicking on `}<strong parentName="p">{`+`}</strong>{` will allow linking a term with the current term.`}</p>
    <Img src={`/images/markdown/terms_related_terms.png`} mdxType="Img" />
    <h5 {...{
      "id": "categorize-a-term"
    }}>{`Categorize a term`}</h5>
    <p>{`Click on `}<strong parentName="p">{`+`}</strong>{` next to categories label to categorize a term. A modal dialog will be presented for choosing a category.`}</p>
    <Img src={`/images/markdown/term_add_category.png`} width="400px" mdxType="Img" />
    <h4 {...{
      "id": "category-view"
    }}><strong parentName="h4">{`Category view`}</strong></h4>
    <p>{`When the toggle switch is on category, the panel will list down all glossaries along-with the category hierarchy. Here's a list of possible
interactions under this view.`}</p>
    <ul>
      <li parentName="ul">{`Expanded view`}
        <Img src={`/images/markdown/category_view_2.png`} width="300px" mdxType="Img" />
      </li>
    </ul>
    <h5 {...{
      "id": "category-context-menu"
    }}>{`Category context menu`}</h5>
    <p>{`Clicking on `}<strong parentName="p">{`ellipsis (...)`}</strong>{` next to the category name will present a category context menu.`}</p>
    <ul>
      <li parentName="ul">{`To create a new category`}</li>
    </ul>
    <Img src={`/images/markdown/category_view_glossary_context.png`} width="600px" mdxType="Img" />
    <ul>
      <li parentName="ul">{`To create a sub-category or delete a category`}</li>
    </ul>
    <Img src={`/images/markdown/category_view_category_context_1.png`} width="600px" mdxType="Img" />
    <h5 {...{
      "id": "category-detail-page"
    }}>{`Category detail page`}</h5>
    <p>{`Once a category is selected, the details will be presented in the right pane.`}</p>
    <Img src={`/images/markdown/category_details_with_terms.png`} width="600px" mdxType="Img" />
    <h5 {...{
      "id": "categorize-term"
    }}>{`Categorize term`}</h5>
    <p>{`Click on `}<strong parentName="p">{`+`}</strong>{` next to the terms label to link a term under selected category.`}</p>
    <Img src={`/images/markdown/category_add_term.png`} width="600px" mdxType="Img" />
    <Img src={`/images/markdown/category_add_term_1.png`} width="400px" mdxType="Img" />
    <Img src={`/images/markdown/category_add_term_2.png`} width="400px" mdxType="Img" />
    <h4 {...{
      "id": "term-assignment-flow"
    }}><strong parentName="h4">{`Term assignment flow`}</strong></h4>
    <p>{`Terms can be assigned to an entity either from the results page or entity details page.`}</p>
    <h5 {...{
      "id": "assign-term"
    }}>{`Assign term`}</h5>
    <p>{`Click on `}<strong parentName="p">{`+`}</strong>{` under "terms" column (if associating term from search page)`}</p>
    <Img src={`/images/markdown/entity_search_add_term.png`} mdxType="Img" />
    <p>{`Click on `}<strong parentName="p">{`+`}</strong>{` next to "terms" label (if viewing a specific entity details)`}</p>
    <Img src={`/images/markdown/entity_details_add_term.png`} mdxType="Img" />
    <p>{`Both the actions will present the following modal, follow prompts on screen to complete term assignment.`}</p>
    <Img src={`/images/markdown/entity_add_term_modal.png`} width="400px" mdxType="Img" />
    <h5 {...{
      "id": "propagated-classification"
    }}>{`Propagated classification`}</h5>
    <p>{`If a term has classification then the entity is has been assigned inherits the same.`}</p>
    <Img src={`/images/markdown/term_details_with_classification.png`} mdxType="Img" />
    <Img src={`/images/markdown/entity_assigned_term_with_tag.png`} width="600px" mdxType="Img" />
    <h4 {...{
      "id": "search-using-a-term"
    }}><strong parentName="h4">{`Search using a term`}</strong></h4>
    <p>{`Apache Atlas basic-search API and UI have been updated to support term as a search criteria. This allows users to find
entities associated with a given term. Basic search UI now includes a new input drop-down for term based searches.`}</p>
    <Img src={`/images/markdown/term_search.png`} width="300px" mdxType="Img" />
    <hr></hr>
    <h4 {...{
      "id": "summary-of-rest-interactions"
    }}>{`Summary of REST interactions`}</h4>
    <p>{`Following operations are supported by Atlas, the details of REST interface can be found `}<a parentName="p" {...{
        "href": "api/v2/index.html"
      }}>{`here`}</a></p>
    <h5 {...{
      "id": "json-structure"
    }}>{`JSON structure`}</h5>
    <ul>
      <li parentName="ul">{`Glossary`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{
	"guid": "2f341934-f18c-48b3-aa12-eaa0a2bfce85",
	"qualifiedName": "SampleBank",
	"displayName": "Banking",
	"shortDescription": "Glossary of bank",
	"longDescription": "Glossary of bank - long description",
	"language": "English",
	"usage": "N/A",
	"terms": [
	{
		"termGuid": "502d34f1-b85f-4ad9-9d9f-fe7020ff0acb",
		"relationGuid": "6bb803e4-3af6-4924-aad6-6ad9f95ecd14",
		"displayText": "A savings account"
	}, {
		"termGuid": "e441a540-ee55-4fc8-8eaf-4b9943d8929c",
		"relationGuid": "dbc46795-76ff-4f68-9043-be0eff0bc0f3",
		"displayText": "15-30 yr mortgage"
	}, {
		"termGuid": "998e3692-51a8-47fe-b3a0-0d9f794437eb",
		"relationGuid": "0dcd31b9-a81c-4185-ad4b-9209a97c305b",
		"displayText": "A checking account"
	}, {
		"termGuid": "c4e2b956-2589-4648-8596-240d3bea5e44",
		"relationGuid": "e71c4a5d-694b-47a5-a41e-126ade857279",
		"displayText": "ARM loans"
	}],
	"categories": [{
		"categoryGuid": "dd94859e-7453-4bc9-b634-a17fc14590f8",
		"parentCategoryGuid": "e6a3df1f-5670-4f9e-84da-91f77d008ce3",
		"relationGuid": "a0b7da02-1ccd-4415-bc54-3d0cdb8857e7",
		"displayText": "Accounts"
	}, {
		"categoryGuid": "e6a3df1f-5670-4f9e-84da-91f77d008ce3",
		"relationGuid": "0e84a358-a4aa-4bd3-b806-497a6962ae1d",
		"displayText": "Customer"
	}, {
		"categoryGuid": "7f041401-de8c-443f-a3b7-7bf5a910ff6f",
		"parentCategoryGuid": "e6a3df1f-5670-4f9e-84da-91f77d008ce3",
		"relationGuid": "7757b031-4e25-43a8-bf77-946f7f06c67a",
		"displayText": "Loans"
	}]
}`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Term`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{
	"guid": "e441a540-ee55-4fc8-8eaf-4b9943d8929c",
	"qualifiedName": "fixed_mtg@SampleBank",
	"displayName": "15-30 yr mortgage",
	"shortDescription": "Short description",
	"longDescription": "Long description",
	"examples": ["N/A"],
	"abbreviation": "FMTG",
	"anchor": {
		"glossaryGuid": "2f341934-f18c-48b3-aa12-eaa0a2bfce85",
		"relationGuid": "dbc46795-76ff-4f68-9043-be0eff0bc0f3"
	},
	"categories": [{
		"categoryGuid": "7f041401-de8c-443f-a3b7-7bf5a910ff6f",
		"relationGuid": "b4cddd33-7b0c-41e2-9324-afe549ec6ada",
		"displayText": "Loans"
	}],
    "seeAlso"           : [],
    "synonyms"          : [],
    "antonyms"          : [],
    "replacedBy"        : [],
    "replacementTerms"  : [],
    "translationTerms"  : [],
    "translatedTerms"   : [],
    "isA"               : [],
    "classifies"        : [],
    "preferredTerms"    : [],
    "preferredToTerms": [ {
                           "termGuid"   : "c4e2b956-2589-4648-8596-240d3bea5e44",
                           "displayText": "ARM Loans"
                         }]
}`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Category`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{
	"guid": "7f041401-de8c-443f-a3b7-7bf5a910ff6f",
	"qualifiedName": "Loans.Customer@HortoniaBank",
	"displayName": "Loans",
	"shortDescription": "Loan categorization",
	"anchor": {
		"glossaryGuid": "2f341934-f18c-48b3-aa12-eaa0a2bfce85",
		"relationGuid": "7757b031-4e25-43a8-bf77-946f7f06c67a"
	},
	"parentCategory": {
		"categoryGuid": "e6a3df1f-5670-4f9e-84da-91f77d008ce3",
		"relationGuid": "8a0a8e11-0bb5-483b-b7d6-cfe0b1d55ef6"
	},
	"childrenCategories" : [],
	"terms": [{
		"termGuid": "e441a540-ee55-4fc8-8eaf-4b9943d8929c",
		"relationGuid": "b4cddd33-7b0c-41e2-9324-afe549ec6ada",
		"displayText": "15-30 yr mortgage"
	}, {
		"termGuid": "c4e2b956-2589-4648-8596-240d3bea5e44",
		"relationGuid": "8db1e784-4f04-4eda-9a58-6c9535a95451",
		"displayText": "ARM loans"
	}]
}`}
    </SyntaxHighlighter>
    <h5 {...{
      "id": "create-operations"
    }}>{`CREATE operations`}</h5>
    <ol>
      <li parentName="ol">{`Create a glossary`}</li>
      <li parentName="ol">{`Create a term`}</li>
      <li parentName="ol">{`Create a categorized term`}</li>
      <li parentName="ol">{`Create term with relations`}</li>
      <li parentName="ol">{`Create a category`}</li>
      <li parentName="ol">{`Create a category with hierarchy`}</li>
      <li parentName="ol">{`Create category and categorize term(s)`}</li>
      <li parentName="ol">{`Assign term to entities`}</li>
    </ol>
    <p><strong parentName="p">{`NOTE:`}</strong></p>
    <ul>
      <li parentName="ul">{`During create operations glossary, term and category get an auto assigned GUID and qualifiedName.`}</li>
      <li parentName="ul">{`To create a category with children, the children MUST be created beforehand.`}</li>
      <li parentName="ul">{`To create a term belonging to a category, the category MUST be created beforehand.`}</li>
      <li parentName="ul">{`To create term with relations, related term(s) MUST be created beforehand.`}</li>
    </ul>
    <hr></hr>
    <h5 {...{
      "id": "read-operations"
    }}>{`READ operations`}</h5>
    <ol>
      <li parentName="ol">{`Get glossary by GUID - Gives all terms and categories (headers) belonging to the glossary.`}</li>
      <li parentName="ol">{`Get all Glossaries - Gives all glossaries with terms and categories (headers) belonging to the respective glossary.`}</li>
      <li parentName="ol">{`Get a term by GUID - Gives details about the term, categories it belongs to (if any) and any related term(s).`}</li>
      <li parentName="ol">{`Get a category by GUID - Gives details about the category, category hierarchy (if any) and term(s) belonging to the category.`}</li>
      <li parentName="ol">{`Get all terms of a given glossary - Gives all terms (with details as mentioned in #3) belonging to given glossary.`}</li>
      <li parentName="ol">{`Get all categories of a given glossary - Gives all categories (with details as mentioned in #4) belonging to given glossary.`}</li>
      <li parentName="ol">{`Get all terms related to given term - Gives all terms related/linked to the given term.`}</li>
      <li parentName="ol">{`Get all categories related to a given category (parent and children)`}</li>
      <li parentName="ol">{`Get all terms for a given category`}</li>
    </ol>
    <hr></hr>
    <h5 {...{
      "id": "update-operations"
    }}>{`UPDATE operations`}</h5>
    <ol>
      <li parentName="ol">{`Partial update of glossary`}</li>
      <li parentName="ol">{`Partial update of term`}</li>
      <li parentName="ol">{`Partial update of category`}</li>
      <li parentName="ol">{`Update a given glossary`}</li>
      <li parentName="ol">{`Update a given term`}</li>
      <li parentName="ol">{`Update a given category`}</li>
    </ol>
    <p><strong parentName="p">{`NOTE:`}</strong></p>
    <ul>
      <li parentName="ul">{`Partial update only deals with the `}<strong parentName="li"><em parentName="strong">{`primitive attributes`}</em></strong>{` defined in the Glossary model file.`}</li>
      <li parentName="ul">{`GUID and qualifiedName can't be changed once assigned. Only way to this is to delete and recreate the required object.`}</li>
      <li parentName="ul">{`Anchors `}<strong parentName="li">{`can't`}</strong>{` be removed in any updates`}</li>
      <li parentName="ul">{`Update APIs expect the JSON to be modified `}<strong parentName="li">{`in-place`}</strong>{` after the `}<strong parentName="li">{`GET`}</strong>{` call. Any `}<strong parentName="li"><em parentName="strong">{`missing`}</em></strong>{` attributes/relations will be `}<strong parentName="li"><em parentName="strong">{`deleted`}</em></strong>{`.`}</li>
      <li parentName="ul">{`Any update to category's hierarchy leads to a cascaded update of the hierarchy under it e.g. anchor change would affect all children,
parent change would affect the qualifiedName of self and children.`}</li>
    </ul>
    <hr></hr>
    <h5 {...{
      "id": "delete-operations"
    }}>{`DELETE operations`}</h5>
    <ol>
      <li parentName="ol">{`Delete glossary - Also deletes all categories and terms anchored to given glossary. This delete is `}<strong parentName="li"><em parentName="strong">{`blocked`}</em></strong>{` if any term has been `}<strong parentName="li"><em parentName="strong">{`assigned`}</em></strong>{` to an entity.`}</li>
      <li parentName="ol">{`Delete term - Only deletes the term `}<strong parentName="li"><em parentName="strong">{`if`}</em></strong>{` it's not associated/assigned to any entity.`}</li>
      <li parentName="ol">{`Delete category - `}<strong parentName="li"><em parentName="strong">{`Only`}</em></strong>{` deletes the given category, all children become top-level categories.`}</li>
      <li parentName="ol">{`Remove term assignment from entity`}</li>
    </ol>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;